import { useState, useContext } from "react";
import { AppContext } from "@/App";
import { useSearchParams } from "react-router-dom";
import { useFetchEntities } from "@/reactQueryHooks";
import { useNavigate } from "react-router-dom";
import ExternalAssignmentItem from "./ExternalAssignmentItem";
import ExternalAssignmentNote from "./ExternalAssignmentNote";
import styled from "styled-components";
import { ITourGroupItem } from "./TourGroupsDateSectionItem";
import { Chip, Divider, Checkbox, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ScheduleIcon from "@mui/icons-material/Schedule";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import Drawer from "@mui/material/Drawer";
import TourGroupModal from "./TourGroupModal";
import TourGroupItemDrawerContent from "./TourGroupItemDrawerContent";
import { useAxios } from "@/axiosProvider";

//------------------------------------------------------------------------

const getRowBackgroundColor = (
  isCancelled: boolean,
  allRolesFilled: boolean,
  isSelected: boolean
) => {
  if (isSelected) {
    return "rgba(144, 238, 144, 0.3)"; // Light green for selected
  }
  if (isCancelled) {
    return "#ffe6e6";
  }
  if (allRolesFilled) {
    return "#d7f8d7";
  }
  return "#d0e8ff";
};

const Container = styled.div<{
  isCancelled: boolean;
  allRolesFilled: boolean;
  isNavigatedTourGroup?: boolean;
  isSelected?: boolean;
}>`
  position: relative;
  background-color: ${({ isCancelled, allRolesFilled, isSelected }) =>
    getRowBackgroundColor(isCancelled, allRolesFilled, isSelected || false)};
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
  border: ${({ isNavigatedTourGroup }) =>
    isNavigatedTourGroup ? "2px solid orange" : "none"};

  &:hover::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background-color: #0070f3; /* or any hover color you prefer */
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;

`;

const Column = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

const ExternalAssignmentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 4px;
`;

const ExternalNotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CustomChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 5px;
  background-color: rgba(0, 0, 0, 0.08);
  padding: 5px 8px;
  border-radius: 20px;
  font-size: 0.8rem;
  color: #333;
  font-weight: 600;
  min-width: 50px;
  font-size: 12px;
`;

const CustomIconButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  padding: 5px;
  cursor: pointer;
  &:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

//----------------------------------------------------------------

interface TourGroupItemProps {
  item: ITourGroupItem;
  refetch: () => void;
  isMultiSelectMode?: boolean;
  onItemSelection?: (
    item: {
      option_id: string;
      start_time_id: string;
      date: string;
      tour_group_id: string;
      product_id: string;
    },
    selected: boolean
  ) => void;
  isSelected?: boolean;
  date: string;
}

const TourGroupItem = ({
  item,
  refetch,
  isMultiSelectMode = false,
  onItemSelection = () => {},
  isSelected = false,
  date,
}: TourGroupItemProps) => {
  const { setDayPlannerDate, setSearchTerm } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNavigatedTourGroup =
    searchParams.get("tourGroupId") === item.tour_group_id;
  const { user } = useAxios();
  const userIsAdmin = user.isAdmin;
  const userIsPermittedToEditGuidePlan =
    user.permissions.editGuidePlan || userIsAdmin;

  const [isTourGroupModalShown, setShowTourGroupModal] = useState(false);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  const { data: entities } = useFetchEntities() as any;

  const product = entities?.data?.products?.find(
    (p: any) => p._id === item.product_id
  );
  const option = product?.options.find((o: any) => o._id === item.option_id);
  const startTime = product?.start_times.find(
    (st: any) => st._id === item.start_time_id
  );

  const hasNotes = item?.external_assignments_notes?.length > 0;

  const handleCheckboxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const selectionItem = {
      option_id: item.option_id,
      start_time_id: item.start_time_id,
      date: date,
      tour_group_id: item.tour_group_id,
      product_id: item.product_id,
    };
    onItemSelection(selectionItem, !isSelected);
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    if (isMultiSelectMode) {
      e.stopPropagation();
      const selectionItem = {
        option_id: item.option_id,
        start_time_id: item.start_time_id,
        date: date,
        tour_group_id: item.tour_group_id,
        product_id: item.product_id,
      };
      onItemSelection(selectionItem, !isSelected);
    } else if (userIsPermittedToEditGuidePlan) {
      e.stopPropagation();
      setOpenDrawer(true);
    } else {
      alert("You are not permitted to edit the guide plan");
    }
  };

  return (
    <Container
      isNavigatedTourGroup={isNavigatedTourGroup}
      isCancelled={item.isCancelled}
      allRolesFilled={item.allRolesFilled}
      isSelected={isSelected}
      onClick={handleContainerClick}
    >
      <Row>
        <Column>
          {isMultiSelectMode && (
            <Checkbox
              checked={isSelected}
              onClick={handleCheckboxClick}
              size="small"
              sx={{ padding: "0 8px 0 0" }}
            />
          )}
          {item.allRolesFilled && !item.isCancelled ? (
            <CheckCircleRoundedIcon sx={{ color: "green", fontSize: "16px" }} />
          ) : item.isCancelled ? (
            <CancelIcon sx={{ color: "red", fontSize: "16px" }} />
          ) : (
            <HourglassTopIcon sx={{ color: "dodgerblue", fontSize: "16px" }} />
          )}
          <span
            style={{
              fontWeight: 600,
            }}
          >
            {product?.title || "N/A"}
          </span>
          <CustomChip
            style={{
              fontSize: "11px",
            }}
          >
            <ScheduleIcon
              fontSize={"small"}
              sx={{
                fontSize: "15px",
              }}
            />
            {startTime?.time_slot}
          </CustomChip>

          <CustomChip
            style={{
              fontSize: "11px",
              fontWeight: 400,
            }}
          >
            {option?.title}
          </CustomChip>

          <CustomChip
            style={{
              fontSize: "11px",
              fontWeight: 400,
            }}
          >
            <span>Group #</span>
            {item?.index}
          </CustomChip>
          <Tooltip title="See tour group" placement="top" arrow>
            <CustomIconButton
              onClick={(e) => {
                e.stopPropagation();
                setShowTourGroupModal(true);
              }}
            >
              <VisibilityIcon
                sx={{
                  fontSize: "14px",
                  color: "dodgerblue",
                }}
              />
            </CustomIconButton>
          </Tooltip>
          <Tooltip title="Navigate to Day Planner" placement="top" arrow>
            <CustomIconButton
              onClick={(e) => {
                e.stopPropagation();
                setDayPlannerDate(date);
                navigate("/day_planner");
              }}
            >
              <OpenInNewIcon
                sx={{
                  fontSize: "13px",
                  color: "dodgerblue",
                }}
              />
            </CustomIconButton>
          </Tooltip>
        </Column>
        <Column
          style={{
            alignSelf: "flex-start",
          }}
        >
          {item.isCancelled && item?.allRolesFilled && (
            <Chip
              label="Cancelled"
              size="small"
              sx={{
                backgroundColor: "rgba(255, 0, 0, 0.2)",
              }}
            />
          )}
          {!item.isCancelled && (
            <CustomChip>
              <PeopleAltIcon
                sx={{
                  color: "grey",
                  fontSize: "15px",
                }}
              />
              <span>{item?.seatsCount}</span>
            </CustomChip>
          )}
        </Column>
      </Row>

      {item.external_assignments && item.external_assignments.length > 0 && (
        <ExternalAssignmentsContainer>
          {item.external_assignments.map((assignment, index) => (
            <ExternalAssignmentItem
              isCancelled={item.isCancelled}
              key={`${assignment.user_id}-${index}`}
              item={assignment}
              tourGroupId={item.tour_group_id}
            />
          ))}
        </ExternalAssignmentsContainer>
      )}

      {hasNotes && (
        <>
          {item.external_assignments &&
            item.external_assignments.length > 0 && <Divider />}
          <ExternalNotesContainer>
            {item?.external_assignments_notes?.map(
              (note: any, index: number) => (
                <ExternalAssignmentNote
                  key={`note-${index}`}
                  user_id={note.user_id}
                  body={note.body}
                  date={note.date.toString()}
                />
              )
            )}
          </ExternalNotesContainer>
        </>
      )}

      {isTourGroupModalShown && (
        <TourGroupModal
          close={() => {
            setShowTourGroupModal(false);
          }}
          id={item?.tour_group_id}
        />
      )}
      <Drawer
        open={openDrawer}
        onClose={(e: React.MouseEvent) => {
          e.stopPropagation();
          setOpenDrawer(false);
        }}
      >
        <TourGroupItemDrawerContent
          refetch={refetch}
          tourGroupId={item.tour_group_id}
          close={(e: React.MouseEvent) => {
            e.stopPropagation();
            setOpenDrawer(false);
          }}
        />
      </Drawer>
    </Container>
  );
};

export default TourGroupItem;
